"use client"

import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react"
import { ImageOff, Loader2 } from "lucide-react"
import type { Photo } from "@/lib/photos"
import { getThumbnailSrcSet, PHOTO_GRID_SIZES } from "@/lib/image-url"
import { cn } from "@/lib/utils"

interface LightboxImageProps {
  photo: Pick<Photo, 'filename' | 'width' | 'height' | 'thumbnailSrc' | 'blurDataURL'>
  previewUrl: string
  /** Widest the preview is requested at; the box never grows past it. */
  previewMaxWidth: number
  onPreviewLoad?: () => void
}

/** Where the box's shape came from. EXIF dimensions can ignore rotation, so real pixels win. */
const SHAPE_FROM_METADATA = 0
const SHAPE_FROM_THUMBNAIL = 1
const SHAPE_FROM_PREVIEW = 2

type Shape = { ratio: number | null; source: number }

function getMetadataShape(photo: Pick<Photo, 'width' | 'height'>): Shape {
  const ratio = photo.width > 0 && photo.height > 0 ? photo.width / photo.height : null
  return { ratio, source: SHAPE_FROM_METADATA }
}

/**
 * The lightbox photo, layered so something is always on screen: the inline blur,
 * then the grid thumbnail (already in the browser cache), then the full preview
 * fading in on top once it arrives.
 */
export function LightboxImage({ photo, previewUrl, previewMaxWidth, onPreviewLoad }: LightboxImageProps) {
  const [shape, setShape] = useState<Shape>(() => getMetadataShape(photo))
  const [maxWidth, setMaxWidth] = useState(previewMaxWidth)
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false)
  const [previewLoaded, setPreviewLoaded] = useState(false)
  const [previewFailed, setPreviewFailed] = useState(false)
  const thumbnailSrc = photo.thumbnailSrc || undefined
  const thumbnailSrcSet = getThumbnailSrcSet(photo.thumbnailSrc)
  // Keep the load callbacks (and the refs built on them) stable across renders.
  const onPreviewLoadRef = useRef(onPreviewLoad)
  useEffect(() => {
    onPreviewLoadRef.current = onPreviewLoad
  }, [onPreviewLoad])

  const takeShapeFrom = useCallback((img: HTMLImageElement, source: number) => {
    if (!img.naturalWidth || !img.naturalHeight) return
    const ratio = img.naturalWidth / img.naturalHeight
    setShape(current => (
      source < current.source || (source === current.source && ratio === current.ratio)
        ? current
        : { ratio, source }
    ))
  }, [])

  const handleThumbnailLoad = useCallback((img: HTMLImageElement) => {
    setThumbnailLoaded(true)
    takeShapeFrom(img, SHAPE_FROM_THUMBNAIL)
  }, [takeShapeFrom])

  const handlePreviewLoad = useCallback((img: HTMLImageElement) => {
    takeShapeFrom(img, SHAPE_FROM_PREVIEW)
    // Small originals aren't enlarged by the server, so don't stretch them here either.
    if (img.naturalWidth) setMaxWidth(width => Math.min(width, img.naturalWidth))
    setPreviewLoaded(true)
    onPreviewLoadRef.current?.()
  }, [takeShapeFrom])

  // Cached images can finish before React attaches onLoad.
  const thumbnailRef = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth > 0) handleThumbnailLoad(img)
  }, [handleThumbnailLoad])
  const previewRef = useCallback((img: HTMLImageElement | null) => {
    if (img?.complete && img.naturalWidth > 0) handlePreviewLoad(img)
  }, [handlePreviewLoad])

  const boxStyle: CSSProperties | undefined = shape.ratio
    ? {
      aspectRatio: String(shape.ratio),
      width: `min(92vw, calc(85vh * ${shape.ratio.toFixed(4)}), ${maxWidth}px)`,
    }
    : undefined
  const hasPlaceholder = Boolean(photo.blurDataURL) || thumbnailLoaded

  return (
    <div
      className={cn("relative", !shape.ratio && "min-h-[200px] min-w-[200px]")}
      style={boxStyle}
    >
      {photo.blurDataURL && (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={photo.blurDataURL}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full scale-105 object-contain blur-lg"
        />
      )}
      {thumbnailSrc && (
        // Same src/srcset/sizes as the grid card, so the browser reuses the copy it already has.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={thumbnailRef}
          src={thumbnailSrc}
          srcSet={thumbnailSrcSet}
          sizes={thumbnailSrcSet ? PHOTO_GRID_SIZES : undefined}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          onLoad={event => handleThumbnailLoad(event.currentTarget)}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
      {!previewFailed && (
        // Watermarked by default; the original preview only when the album offers original-only downloads.
        // eslint-disable-next-line @next/next/no-img-element
        <img
          ref={previewRef}
          src={previewUrl}
          alt={photo.filename}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          onLoad={event => handlePreviewLoad(event.currentTarget)}
          onError={() => setPreviewFailed(true)}
          className={cn(
            "absolute inset-0 h-full w-full object-contain transition-opacity duration-300",
            previewLoaded ? "opacity-100" : "opacity-0"
          )}
        />
      )}

      {!previewLoaded && !previewFailed && !hasPlaceholder && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Loader2 className="h-10 w-10 animate-spin text-white/50" />
        </div>
      )}
      {previewFailed && (
        thumbnailLoaded ? (
          <p className="absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/60 px-3 py-1 text-xs text-white/80">
            Couldn&apos;t load the full-size photo
          </p>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/60" role="img" aria-label="Photo unavailable">
            <ImageOff className="h-10 w-10" />
            <span className="text-sm">Photo unavailable</span>
          </div>
        )
      )}
    </div>
  )
}
